import { XCircleIcon } from "@phosphor-icons/react";
import { useNotification, useTranslate } from "@refinedev/core";
import { Flex, Modal } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import { FaceProfileCreateForm } from "pages/media/components/FaceProfileCreateForm";
import { FaceProfileResponse, VoiceProfileResponse } from "pages/media/types";
import { VoiceProfileCreatePage } from "pages/media/voices/create";
import React, { useState } from "react";
import { FaceProfileCreationResponse } from "./types";
import { radius } from "@libertify/ui";

export type AvatarCloneModalType = "voice" | "face" | null;

interface AvatarCloneVoiceAndFaceModalProps {
  currentModal: AvatarCloneModalType;
  setCurrentModal: React.Dispatch<React.SetStateAction<AvatarCloneModalType>>;
  open: ReturnType<typeof useNotification>["open"];
  setSelectedVoice: React.Dispatch<
    React.SetStateAction<VoiceProfileResponse | undefined>
  >;
  setClonedVoiceResponse: React.Dispatch<
    React.SetStateAction<VoiceProfileResponse | undefined>
  >;
  setClonedFaceResponse: React.Dispatch<
    React.SetStateAction<FaceProfileCreationResponse | undefined>
  >;
  setSelectedFace: React.Dispatch<
    React.SetStateAction<Partial<FaceProfileResponse> | undefined>
  >;
  closeModalCallback: React.MutableRefObject<
    (face: Partial<FaceProfileResponse>) => void
  >;
}

export const AvatarCloneVoiceAndFaceModal = ({
  currentModal,
  setCurrentModal,
  open,
  setSelectedVoice,
  setClonedVoiceResponse,
  setClonedFaceResponse,
  setSelectedFace,
  closeModalCallback,
}: AvatarCloneVoiceAndFaceModalProps) => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  // While the voice clone request is in flight we lock the modal closed so
  // the user can't abandon a half-cloned voice profile. useVoiceCloning
  // resets isCreating on Success, Failed, and timeout, which re-enables close.
  const [isCloningVoice, setIsCloningVoice] = useState(false);
  return (
    <Modal
      destroyOnHidden
      maskClosable={!isCloningVoice}
      onCancel={() => {
        if (isCloningVoice) return;
        setCurrentModal(null);
      }}
      closable={!isCloningVoice}
      closeIcon={<XCircleIcon size={32} color={theme.colorPrimary} />}
      footer={false}
      open={!!currentModal}
      styles={{
        content: { borderRadius: radius.box },
        body: {
          padding: 24,
          paddingBottom: 0,
        },
      }}
      width={currentModal === "voice" ? 800 : undefined}
    >
      {currentModal === "voice" && (
        <VoiceProfileCreatePage
          onCreatingChange={setIsCloningVoice}
          onSuccess={(voiceProfileId: string) => {
            setSelectedVoice(undefined);
            // The hook hands us an id (the WS Success event doesn't carry the
            // full profile). The downstream useOne in AvatarDetails fetches
            // the full record, so storing only the id here is sufficient.
            setClonedVoiceResponse({
              id: voiceProfileId,
            } as VoiceProfileResponse);
            setCurrentModal(null);
          }}
        />
      )}
      <Flex justify="center" align="center">
        {currentModal === "face" && (
          <FaceProfileCreateForm
            onSuccess={(face: FaceProfileCreationResponse) => {
              open?.({
                message: t(
                  "components.ContactWithPresenter.PageComponent.thisMayTake"
                ),
                description: t(
                  "components.ContactWithPresenter.PageComponent.wereCloningYour"
                ),
                type: "success",
              });
              face.id = face.face_profile_id;
              setClonedFaceResponse({ ...face, category: "Custom" });
              const newFace = {
                id: face.face_profile_id,
                category: "Custom" as const,
              };
              setSelectedFace(newFace);
              closeModalCallback.current(newFace);
              setCurrentModal(null);
            }}
          />
        )}
      </Flex>
    </Modal>
  );
};
